/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React from 'react'
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  VStack,
  Tooltip
} from '@chakra-ui/react'
import { format } from 'date-fns'
import Card from '@/components/cards/base/Card'
import { formatPrice } from '@/components/license-history/license-history-summary-modal/handlers'
import RevenueValue from './components/RevenueValue'
import EmptyValue from './components/EmptyValue'
import SectionTitle from './components/SectionTitle'

export default function RevenueHistoryTable ({ transactions }) {
  if (!transactions || transactions.length === 0) {
    return (
      <Card>
        <div className="text-center text-gray-500 py-8">
          No transactions yet
        </div>
      </Card>
    )
  }

  return (
      <VStack spacing={4} align="stretch">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Revenue History</h3>
          <Text className="text-sm text-gray-600">
            Showing {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
          </Text>
        </div>

        <div className="overflow-x-auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>Timestamp</Th>
                <Th>SAM ID</Th>
                <Th isNumeric>Deal Price</Th>
                <Th>License Type</Th>
                <Th>Buyer</Th>
                <Th isNumeric>Deal Revenue</Th>
                <Th isNumeric>Usage Revenue</Th>
                <Th isNumeric>Total Revenue</Th>
                <Th>Royalties Paid</Th>
              </Tr>
            </Thead>
            <Tbody>
              {transactions.map((transaction, index) => (
                <Tr key={index}>
                  <Td>
                    <Text className="text-sm whitespace-nowrap">
                      {format(new Date(transaction.timestamp), 'MMM dd, yyyy')}
                    </Text>
                    <Text className="text-sm text-gray-500">
                      {format(new Date(transaction.timestamp), 'HH:mm')}
                    </Text>
                  </Td>
                  <Td>
                    { transaction.samId
                      ? <Text className="text-sm">
                          { transaction.samId }
                        </Text>
                      : <EmptyValue value="—"/>
                    }
                  </Td>
                  <Td isNumeric>
                    <Text className="text-sm font-medium">
                      ${formatPrice(transaction.dealPrice)}
                    </Text>
                  </Td>
                  <Td>
                    <EmptyValue value={transaction.licenseType} />
                  </Td>
                  <Td>
                    <VStack spacing={0} align="start">
                      <Text className="text-sm font-medium">
                        {transaction.buyer.nickname || `User ${transaction.buyer.id}`}
                      </Text>
                      <Tooltip label={transaction.buyer.zetoniumId}>
                        <Text className="text-sm text-gray-500 truncate max-w-[120px]">
                          { transaction.buyer.zetoniumId }
                        </Text>
                      </Tooltip>
                    </VStack>
                  </Td>
                  <Td isNumeric>
                    <RevenueValue value={transaction.dealRevenue} />
                  </Td>
                  <Td isNumeric>
                    { transaction.usageRevenue > 0
                      ? <RevenueValue value={transaction.usageRevenue} />
                      : <EmptyValue value="$0.00"/>
                    }
                  </Td>
                  <Td isNumeric>
                    <RevenueValue value={transaction.totalRevenue} isBold={true} />
                  </Td>
                  <Td>
                    <VStack spacing={1} align="start">
                      { transaction.royaltiesPaid.creators.length > 0 &&
                        <VStack spacing={0} align="start">
                          <SectionTitle>
                            Creators:
                          </SectionTitle>
                          {transaction.royaltiesPaid.creators.map((creator, idx) => (
                            <Text key={idx} className="text-sm text-gray-600">
                              {creator.name} - {formatPrice(creator.percentage)}% - ${formatPrice(creator.amount)}
                            </Text>
                          ))}
                        </VStack>
                      }
                      { transaction.royaltiesPaid.publishers.length > 0 &&
                        <VStack spacing={0} align="start">
                          <SectionTitle>
                            Publishers:
                          </SectionTitle>
                          {transaction.royaltiesPaid.publishers.map((publisher, idx) => (
                            <Text key={idx} className="text-sm text-gray-600">
                              {publisher.name} - {formatPrice(publisher.percentage)}% - ${formatPrice(publisher.amount)}
                            </Text>
                          ))}
                        </VStack>
                      }
                    </VStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
      </VStack>
  )
}
